/**
 * External Dependencies
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';

/**
 * Internal Dependencies
 */
import Routes from './Routes';
import PageNavbar from './components/page-navbar';
import PageToasts from './components/page-toasts';
import PageYaybar from './components/page-yaybar';

import { updateAuth as actionUpdateAuth } from './actions';

const $html = window.jQuery('html');
const $body = window.jQuery('body');
let referrerFromAuth = '/';

const EXCLUDED_PATHS = [
  'monitor-l',
  'totem-l',
  'display-l',
  'ambulatoriale-light',
  'ambulatoriale-code',
  'ambulatoriale-code-full',
  'reset-password',
];

const NAVBAR_EXCLUDED_PATHS = ['success', 'error', ...EXCLUDED_PATHS];

const isSignPage = pathname => {
  return pathname === '/sign-in' || pathname === '/sign-up';
};

/**
 * Component PageWrap
 */
const PageWrap = () => {
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settings);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const prevAuthRef = useRef(auth);
  const prevSettingsRef = useRef(settings);
  const prevLocationRef = useRef(location);
  const isFirstRender = useRef(true);

  const updateAuth = useCallback(
    payload => dispatch(actionUpdateAuth(payload)),
    [dispatch]
  );

  // Auth check effect
  useEffect(() => {
    const prevAuth = prevAuthRef.current;

    if (auth.referrer && auth.referrer !== '/sign-in') {
      referrerFromAuth = auth.referrer;
    }

    let newRedirect = false;
    const referrerUrl = auth.referrer || '/';

    // Redirect from Auth page
    if (isSignPage(location.pathname) && auth.token) {
      newRedirect =
        referrerUrl === '/sign-in'
          ? referrerFromAuth || '/'
          : referrerUrl || '/';
    }
    // Redirect to Auth page
    else if (
      !isSignPage(location.pathname) &&
      !auth.token &&
      !EXCLUDED_PATHS.includes(location.pathname.split('/')[1])
    ) {
      newRedirect = '/sign-in';
    }
    // Redirect only when crossing login boundary (not on token refresh)
    else if (!isFirstRender.current && auth.token !== prevAuth.token) {
      if (auth.token && !prevAuth.token) {
        newRedirect = referrerUrl;
      } else if (!auth.token && prevAuth.token) {
        newRedirect = `/sign-in?redirectTo=${referrerUrl}`;
      }
    }

    // Redirect
    if (newRedirect) {
      updateAuth({ referrer: location.pathname });
      navigate(newRedirect);
    }

    prevAuthRef.current = auth;
  }, [auth, updateAuth, navigate, location.pathname]);

  // Global settings effect
  useEffect(() => {
    const prevSettings = prevSettingsRef.current;

    // Night mode
    if (
      !isFirstRender.current &&
      prevSettings.night_mode !== settings.night_mode
    ) {
      if (settings.night_mode) {
        $html.addClass('rui-night-mode');
        import('./style-night.scss');
      } else {
        $html.removeClass('rui-night-mode');
      }
    }
    if (isFirstRender.current && settings.night_mode) {
      $html.addClass('rui-night-mode');
      import('./style-night.scss');
    }

    // Spotlight mode
    if (
      !isFirstRender.current &&
      prevSettings.spotlight_mode !== settings.spotlight_mode
    ) {
      if (settings.spotlight_mode) {
        $body.addClass('rui-spotlightmode');
      } else {
        $body.removeClass('rui-spotlightmode');
      }
    }
    if (isFirstRender.current && settings.spotlight_mode) {
      $body.addClass('rui-spotlightmode');
    }

    // Section lines
    if (
      !isFirstRender.current &&
      prevSettings.show_section_lines !== settings.show_section_lines
    ) {
      if (settings.show_section_lines) {
        $body.addClass('rui-section-lines');
      } else {
        $body.removeClass('rui-section-lines');
      }
    }
    if (isFirstRender.current && settings.show_section_lines) {
      $body.addClass('rui-section-lines');
    }

    // Sidebar small
    if (
      !isFirstRender.current &&
      prevSettings.sidebar_small !== settings.sidebar_small
    ) {
      if (settings.sidebar_small) {
        $body.addClass('yay-hide');
      } else {
        $body.removeClass('yay-hide');
      }
    }
    if (isFirstRender.current && settings.sidebar_small) {
      $body.addClass('yay-hide');
    }

    prevSettingsRef.current = settings;
  }, [settings]);

  // Scroll to top effect
  useEffect(() => {
    const prevLocation = prevLocationRef.current;

    if (!isFirstRender.current && location.pathname !== prevLocation.pathname) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    prevLocationRef.current = location;
  }, [location.pathname]);

  // Mark first render as complete
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const hasValidToken = auth.token && auth.token !== '';
  const isExcludedPath =
    isSignPage(location.pathname) ||
    NAVBAR_EXCLUDED_PATHS.includes(location.pathname.split('/')[1]);

  const showNavbar = hasValidToken && !isExcludedPath;

  return (
    <>
      <PageToasts />
      {showNavbar ? (
        <>
          <PageYaybar />
          <PageNavbar />
        </>
      ) : null}
      <Routes location={location} />
    </>
  );
};

/**
 * Component App
 */
const App = () => {
  return (
    <Provider store={window.store}>
      <BrowserRouter>
        <PageWrap />
      </BrowserRouter>
    </Provider>
  );
};

export default App;
